import DashboardStore from "../DashboardStore";
import { observer } from "mobx-react";
import React, { FC, useEffect, useState } from "react";
import RecentActivityIcon from "../../../../../../images/activity-dashboard/calendar-slate-blue.svg";
import { SensexBox, ThreeColumns } from "./DashboardMain.styles";
import { DashboardService } from "../../../shared/services";
import {
  AuthService,
  CustomLabel,
  FlexContainer,
  Loader,
  ToastService,
  UtilityMethods,
} from "@mindmemobile/mm-shared-global";
import { ActivitiesDataModel, ActivitiesModel } from "../../../shared/models";
import { AccountSettingsStore } from "../../../shared/store";

type IProps = {
  dashboardStore: DashboardStore;
  merchantAccountId: string;
  /**
   * Expanded layout (wide card with a dedicated Today / This Month header row).
   * Only used for NMI merchant accounts, where the widget sits next to the
   * Subscription Breakdown card. Fiserv keeps the original 50/50 layout.
   */
  isExpanded?: boolean;
};
const ActivityWidget: FC<IProps> = ({
  dashboardStore,
  merchantAccountId,
  isExpanded = false,
}) => {
  const [showLoader, setLoader] = useState(false);
  useEffect(() => {
    if (!merchantAccountId) return;
    setLoader(true);
    DashboardService.getRecentActivities(
      AuthService.authData.accountId,
      merchantAccountId,
    ).subscribe({
      next: (response) => {
        setLoader(false);
        const mappedData = ActivitiesModel.deserializeAPIModel(response.result);
        dashboardStore.updateActivities(mappedData);
      },
      error: () => {
        ToastService.showError("Error Occured");
      },
    });
  }, [merchantAccountId, dashboardStore]);

  if (showLoader || !dashboardStore.activityWidgetData)
    return <>{new Loader(true, "", "330px").loaderComponent}</>;

  function getComparisonCell(data: ActivitiesDataModel, amount = false) {
    const value = amount
      ? `$${AccountSettingsStore.formatAmount(data.value, true)}`
      : `${data.value}`;
    return (
      <FlexContainer
        spacing={isExpanded ? 8 : 10}
        alignItems="center"
        justifyContent={isExpanded ? "flex-start" : "flex-end"}
        className="recent-activity-block"
      >
        <div className="sensex-value-wrapper">
          <CustomLabel
            fontSize={"18px"}
            fontWeight={"600"}
            color="#2C3E50"
            lineHeight="26px"
          >
            {" "}
            {value}
          </CustomLabel>
        </div>
        <div className="sensex-box-wrapper">
          <div style={{ display: "inline-block" }}>
            <SensexBox up={data.up} down={!data.up}>
              <i
                className={`fa fa-solid fa-sort-${data.up ? "up" : "down"}`}
              ></i>
              {Math.round(UtilityMethods.formatDecimalNumber(data.percentage))}%
            </SensexBox>
          </div>
        </div>
      </FlexContainer>
    );
  }

  function headerCell(label: string) {
    return (
      <FlexContainer
        spacing={8}
        alignItems="center"
        justifyContent="flex-start"
        className="recent-activity-block recent-activity-header"
      >
        <div className="sensex-value-wrapper">
          <CustomLabel textAlign="center">{label}</CustomLabel>
        </div>
        <div className="sensex-box-wrapper" />
      </FlexContainer>
    );
  }

  function row(data1: string, data2: React.ReactNode, data3: React.ReactNode) {
    if (!isExpanded) {
      return (
        <FlexContainer justifyContent="space-between" padding="0px 0px 20px">
          <ThreeColumns>
            <CustomLabel>{data1}</CustomLabel>
          </ThreeColumns>
          <ThreeColumns className="text-center">{data2}</ThreeColumns>
          <ThreeColumns className="text-center">{data3}</ThreeColumns>
        </FlexContainer>
      );
    }
    return (
      <FlexContainer
        alignItems="center"
        spacing={20}
        padding="0px 0px 20px"
        className="recent-activity-row"
      >
        <div className="ra-label">
          <CustomLabel>{data1}</CustomLabel>
        </div>
        <div className="ra-metric">{data2}</div>
        <div className="ra-metric">{data3}</div>
      </FlexContainer>
    );
  }
  const grossVolume = dashboardStore.activityWidgetData.grossVolume;
  const netVolume = dashboardStore.activityWidgetData.netVolume;
  const payments = dashboardStore.activityWidgetData.payments;
  const newCustomers = dashboardStore.activityWidgetData.newCustomers;
  const newSubscriptions = dashboardStore.activityWidgetData.newSubscriptions;
  return (
    <div
      className={
        isExpanded ? "activity-widget-expanded" : "activity-widget-classic"
      }
    >
      <FlexContainer alignItems="center" spacing={10} padding="0px 0px 30px">
        <img src={RecentActivityIcon} />
        <div className="activity-card-header">Recent Activity</div>
      </FlexContainer>
      {isExpanded
        ? row("", headerCell("Today"), headerCell("This Month"))
        : row(
            "",
            <CustomLabel textAlign="center">Today</CustomLabel>,
            <CustomLabel textAlign="center">This Month</CustomLabel>,
          )}
      {row(
        "Gross  Volume",
        getComparisonCell(grossVolume.today, true),
        getComparisonCell(grossVolume.thisPeriod, true),
      )}
      {row(
        "Net  Volume",
        getComparisonCell(netVolume.today, true),
        getComparisonCell(netVolume.thisPeriod, true),
      )}
      {row(
        "Payments",
        getComparisonCell(payments.today),
        getComparisonCell(payments.thisPeriod),
      )}
      {row(
        "New Customers",
        getComparisonCell(newCustomers.today),
        getComparisonCell(newCustomers.thisPeriod),
      )}
      {row(
        "New Subscriptions",
        getComparisonCell(newSubscriptions.today),
        getComparisonCell(newSubscriptions.thisPeriod),
      )}
    </div>
  );
};

export default observer(ActivityWidget);
